<script setup>
import { ref, watch } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import {
    PhCaretRight, PhArrowRight, PhCalendarBlank,
    PhPower, PhX, PhPencilSimple,
} from '@phosphor-icons/vue'

import Layout       from '@pages/Shared/layouts/StaffLayout.vue'
import Table        from '@pages/Shared/ui/Table.vue'
import Prompt       from '@pages/Shared/Prompt.vue'
import SessionModal from './components/SessionModal.vue'

import { formatDate } from '@/helpers.js'
import { flashToast } from '@/toast.js'

/* -------------------- PROPS -------------------- */
const props = defineProps({
    academic_sessions: Object,
    filters:           Object,
})

/* -------------------- TABLE COLUMNS -------------------- */
const columns = [
    { key: 'index',       label: '#',          type: 'index' },
    { key: 'name',        label: 'Session',    type: 'slot'  },
    { key: 'start_date',  label: 'Start Date', type: 'slot'  },
    { key: 'range_arrow', label: '',           type: 'slot'  },
    { key: 'end_date',    label: 'End Date',   type: 'slot'  },
    { key: 'status',      label: 'Status',     type: 'slot'  },
    { key: 'actions',     label: '',           type: 'slot'  },
]

/* -------------------- FILTERS -------------------- */
const filters = ref({ search: null, status: null, ...props.filters })

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/staff/academic-sessions', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value)      => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

/* -------------------- EXPANDED ROWS -------------------- */
const expandedId   = ref(null)
const toggleExpand = (id) => { expandedId.value = expandedId.value === id ? null : id }

/* -------------------- SESSION MODAL -------------------- */
const sessionModalRef = ref(null)
const editSession     = (id) => {
    const session = props.academic_sessions.data.find(s => s.id === id)
    if (session) sessionModalRef.value?.open(session)
}

/* -------------------- CONFIRM PROMPT -------------------- */
const confirmModal = ref({
    isShow:       false,
    title:        '',
    message:      '',
    confirmText:  'Confirm',
    confirmColor: 'red',
    onConfirm:    null,
})

const showConfirm = ({ title, message, confirmText, confirmColor = 'red', onConfirm }) => {
    confirmModal.value = { isShow: true, title, message, confirmText, confirmColor, onConfirm }
}

// Close session — destructive (red)
const confirmCloseSession = (id) => showConfirm({
    title:        'Close Session',
    message:      'Do you want to close this academic session?',
    confirmText:  'Close',
    confirmColor: 'red',
    onConfirm:    () => { closeSession(id); confirmModal.value.isShow = false },
})

// Activate term — positive staff action
const confirmActivateTerm = (id) => showConfirm({
    title:        'Activate Term',
    message:      'Do you want to activate this term?',
    confirmText:  'Activate',
    confirmColor: 'staff',
    onConfirm:    () => { activateTerm(id); confirmModal.value.isShow = false },
})

// Close term — destructive (red)
const confirmCloseTerm = (id) => showConfirm({
    title:        'Close Term',
    message:      'Do you want to close this term?',
    confirmText:  'Close',
    confirmColor: 'red',
    onConfirm:    () => { closeTerm(id); confirmModal.value.isShow = false },
})

const closeSession = (id) =>
    router.patch(`/staff/academic-sessions/${id}/close`, {}, { onSuccess: (page) => flashToast(page.props.flash) })

const activateTerm = (id) =>
    router.patch(`/staff/academic-terms/${id}/activate`, {}, { preserveState: true, onSuccess: (page) => flashToast(page.props.flash) })

const closeTerm = (id) =>
    router.patch(`/staff/academic-terms/${id}/close`, {}, { preserveState: true, onSuccess: (page) => flashToast(page.props.flash) })

/* -------------------- HELPERS -------------------- */
const getSessionStatus = (session) => {
    if (session.is_active) return 'Active'
    if (new Date(session.starts_at) > new Date()) return 'Upcoming'
    return 'Closed'
}

const getActiveTermIndex = (terms) => terms.findIndex(t => t.is_active)

const getTermStatus = (session, terms, index, isActive) => {
    if (!session.is_active) return 'Closed'
    if (isActive) return 'Active'
    const activeIndex = getActiveTermIndex(terms)
    if (activeIndex !== -1 && index < activeIndex) return 'Closed'
    return 'Upcoming'
}

const getTermStatusClass = (session, terms, index, isActive) => {
    const s = getTermStatus(session, terms, index, isActive)
    if (s === 'Active')   return 'bg-green-50 text-green-700 border-green-100'
    if (s === 'Upcoming') return 'bg-yellow-50 text-yellow-700 border-yellow-100'
    return 'bg-gray-50 text-gray-500 border-gray-200'
}

const getTermDotClass = (session, terms, index, isActive) => {
    const s = getTermStatus(session, terms, index, isActive)
    if (s === 'Active')   return 'bg-green-500'
    if (s === 'Upcoming') return 'bg-yellow-400'
    return 'bg-gray-400'
}

const isTermUpcoming = (terms, index) => {
    const activeIndex = getActiveTermIndex(terms)
    return activeIndex === -1 ? index === 0 : index > activeIndex
}
</script>

<template>
    <Layout>
        <Head title="Academic Sessions" />

        <Prompt
            :title="confirmModal.title"
            :message="confirmModal.message"
            :is-show="confirmModal.isShow"
            :confirm-text="confirmModal.confirmText"
            :confirm-color="confirmModal.confirmColor"
            cancel-text="Cancel"
            @delete="confirmModal.onConfirm && confirmModal.onConfirm()"
            @cancel="confirmModal.isShow = false"
        />

        <SessionModal ref="sessionModalRef" />

        <!-- Header -->
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Academic Sessions</h2>
                <p class="text-sm text-gray-500 mt-0.5">View and manage academic term activities</p>
            </div>
        </div>

        <!-- Table -->
        <Table
            :data="academic_sessions"
            :columns="columns"
            variant="staff"
            row-key="id"
            :expanded-id="expandedId"
            :actions="{ view: false, edit: false, delete: false }"
            :filters="[
                {
                    key:     'status',
                    label:   'Status',
                    options: [
                        { label: 'All Statuses', value: null       },
                        { label: 'Active',       value: 'active'   },
                        { label: 'Upcoming',     value: 'upcoming' },
                        { label: 'Closed',       value: 'closed'   },
                    ],
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
        >
            <!-- Session name with expand toggle -->
            <template #name="{ row }">
                <button
                    class="flex items-center gap-1.5 font-semibold text-primary hover:text-primary-light transition"
                    @click="toggleExpand(row.id)"
                >
                    <PhCaretRight :size="14" class="transition-transform shrink-0"
                        :class="expandedId === row.id ? 'rotate-90' : ''" />
                    {{ row.name }}
                </button>
            </template>

            <template #start_date="{ row }">
                <span class="text-sm text-gray-700">{{ formatDate(row.starts_at) }}</span>
            </template>

            <template #range_arrow>
                <PhArrowRight :size="14" class="text-gray-400" />
            </template>

            <template #end_date="{ row }">
                <span class="text-sm text-gray-700">{{ formatDate(row.ends_at) }}</span>
            </template>

            <!-- Status badge -->
            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full border"
                    :class="
                        getSessionStatus(row) === 'Active'   ? 'bg-green-50 text-green-700 border-green-100'    :
                        getSessionStatus(row) === 'Upcoming' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                        'bg-gray-50 text-gray-500 border-gray-200'
                    "
                >
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="
                            getSessionStatus(row) === 'Active'   ? 'bg-green-500'  :
                            getSessionStatus(row) === 'Upcoming' ? 'bg-yellow-400' :
                            'bg-gray-400'
                        "></span>
                    {{ getSessionStatus(row) }}
                </span>
            </template>

            <!-- Row actions -->
            <template #actions="{ row }">
                <div class="flex items-center justify-end gap-1.5">
                    <button v-if="row.is_active"
                        @click="confirmCloseSession(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-200 transition">
                        <PhX :size="13" /> Close
                    </button>

                    <button @click="editSession(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition">
                        <PhPencilSimple :size="13" /> Edit
                    </button>
                </div>
            </template>

            <!-- Expanded: term cards -->
            <template #expanded="{ row }">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div
                        v-for="(term, index) in row.terms" :key="term.id"
                        class="bg-white border border-gray-200 rounded-lg p-4"
                    >
                        <!-- Term header -->
                        <div class="flex items-center justify-between mb-3">
                            <div class="flex items-center gap-2">
                                <PhCalendarBlank :size="14" class="text-primary shrink-0" />
                                <span class="text-sm font-semibold text-gray-800">{{ term.name }}</span>
                            </div>
                            <span
                                class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border"
                                :class="getTermStatusClass(row, row.terms, index, term.is_active)"
                            >
                                <span class="w-1.5 h-1.5 rounded-full"
                                    :class="getTermDotClass(row, row.terms, index, term.is_active)"></span>
                                {{ getTermStatus(row, row.terms, index, term.is_active) }}
                            </span>
                        </div>

                        <!-- Term dates -->
                        <div class="flex items-center gap-2 text-sm text-gray-600 mb-3">
                            <span>{{ formatDate(term.starts_at) }}</span>
                            <PhArrowRight :size="12" class="text-gray-400 shrink-0" />
                            <span>{{ formatDate(term.ends_at) }}</span>
                        </div>

                        <!-- Term actions -->
                        <div class="flex gap-2">
                            <button
                                v-if="!term.is_active && isTermUpcoming(row.terms, index) && row.is_active"
                                @click="confirmActivateTerm(term.id)"
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                                       text-primary bg-primary/10 hover:bg-primary/20 rounded-md border border-primary/20 transition"
                            >
                                <PhPower :size="12" /> Activate
                            </button>

                            <button
                                v-if="term.is_active"
                                @click="confirmCloseTerm(term.id)"
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                                       text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-200 transition"
                            >
                                <PhX :size="12" /> Close
                            </button>
                        </div>
                    </div>

                    <div v-if="!row.terms?.length" class="sm:col-span-3 py-4 text-center text-sm text-gray-400 italic">
                        No terms created for this session
                    </div>
                </div>
            </template>
        </Table>
    </Layout>
</template>